import {
  Box,
  Card,
  CardHeader,
  Container,
  Divider,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { getEmployeeDocumasterDetails, triggerEmployeeSync } from 'src/api/employee';
import EmptyContent from 'src/components/empty-content';
import Iconify from 'src/components/iconify';
import { useSnackbar } from 'src/components/snackbar';
import { EmployeeSyncArea, IEmployeeDocumaster } from 'src/types/employee';

type Props = {
  employeeId: string | undefined;
  employeeName: string | undefined;
  evoExtId: string | null | undefined;
};

type DetailsContainerProp = {
  data: IEmployeeDocumaster | null;
};

function DetailsContainer({ data }: DetailsContainerProp) {
  return (
    <>
      {data ? (
        <Stack
          spacing={2}
          alignItems={{ md: 'flex-start' }}
          direction={{ xs: 'column' }}
          sx={{
            p: 2,
            pt: 0,
            fontSize: 12,
          }}
        >
          <Stack>
            <Box sx={{ color: 'text.disabled' }}>Tag Id</Box>
            {data?.id}
          </Stack>
          <Stack>
            <Box sx={{ color: 'text.disabled' }}>Tag Revision</Box>
            {data?.revision}
          </Stack>
          <Stack>
            <Box sx={{ color: 'text.disabled' }}>Tag Title</Box>
            {data?.title}
          </Stack>
          <>
            {data?.externalIds?.map((item, index) => (
              <Stack
                spacing={2}
                alignItems={{ md: 'flex-start' }}
                direction={{ xs: 'column' }}
                key={`mb${index}`}
                sx={{
                  width: '100%',
                }}
              >
                <Divider
                  sx={{ borderStyle: 'dotted', mt: 0, mb: 0, borderWidth: 1, width: '100%' }}
                />
                <Stack>
                  <Box sx={{ color: 'text.disabled' }}>ExternalId</Box>
                  {item.id}
                </Stack>
                <Stack>
                  <Box sx={{ color: 'text.disabled' }}>ExternalId Title</Box>
                  {item.externalId}
                </Stack>
              </Stack>
            ))}
          </>
          <>
            {data?.classifications ? (
              <Stack
                spacing={2}
                alignItems={{ md: 'flex-start' }}
                direction={{ xs: 'column' }}
                sx={{
                  width: '100%',
                }}
              >
                <Divider
                  sx={{ borderStyle: 'dotted', mt: 0, mb: 0, borderWidth: 1, width: '100%' }}
                />
                <Stack>
                  <Box sx={{ color: 'text.disabled' }}>Classification Id</Box>
                  {data.classifications.id}
                </Stack>
                <Stack>
                  <Box sx={{ color: 'text.disabled' }}>Classification Title</Box>
                  {data.classifications.title}
                </Stack>
              </Stack>
            ) : (
              <>
                <Divider
                  sx={{ borderStyle: 'dotted', mt: 0, mb: 0, borderWidth: 1, width: '100%' }}
                />
                <Box sx={{ color: 'text.disabled' }}>No member group assigned</Box>
              </>
            )}
          </>
        </Stack>
      ) : (
        <EmptyContent
          title="No Data"
          sx={{
            pt: 3,
            pb: 6,
          }}
        />
      )}
    </>
  );
}

export default function EmployeeDetailsDocumaster({ evoExtId, employeeId, employeeName }: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [documasterData, setDocumasterData] = useState<IEmployeeDocumaster | null>(null);

  const loadData = useCallback(() => {
    if (!evoExtId) {
      return;
    }

    setLoading(true);
    getEmployeeDocumasterDetails(evoExtId)
      .then((response) => {
        setDocumasterData(response.data);
      })
      .catch((err) => {
        console.log(err);
        setDocumasterData(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [evoExtId]);

  const triggerSync = useCallback(() => {
    if (!employeeId) {
      return;
    }

    setLoading(true);
    enqueueSnackbar(`Documaster sync triggered for ${employeeName}`);
    triggerEmployeeSync(employeeId, [EmployeeSyncArea.Documaster])
      .then((r) => {
        enqueueSnackbar(`Documaster sync finished for ${employeeName}`, {
          variant: 'success',
        });
        loadData();
      })
      .catch((err) => {
        console.log(err);
        enqueueSnackbar(`Documaster sync failed due to ${err[0]?.errorKey || 'unknown error'}`, {
          variant: 'error',
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [enqueueSnackbar, loadData, employeeId, employeeName]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <Card>
      <CardHeader
        title={
          <Stack spacing={1} direction="row" alignItems="center">
            <Typography variant="h6">Documaster</Typography>
          </Stack>
        }
        action={
          <Stack spacing={0} direction="row">
            <IconButton title="Trigger sync" onClick={() => triggerSync()}>
              <Iconify icon="mdi:account-sync" />
            </IconButton>

            <IconButton title="Refresh data" onClick={() => loadData()} disabled={!evoExtId}>
              <Iconify icon="eva:refresh-outline" />
            </IconButton>
          </Stack>
        }
      />
      <Divider sx={{ borderStyle: 'solid', mt: 2, mb: 2 }} />
      {loading ? (
        <Container maxWidth="lg">
          <LinearProgress key="primary" color="primary" sx={{ mb: 2, width: 1 }} />
        </Container>
      ) : (
        <DetailsContainer data={documasterData} />
      )}
    </Card>
  );
}
